// src/pages/ChatDetail.jsx
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  setDoc, 
  increment
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { enviarNotificacion } from "../utils/notifications";


export async function notifyChat(uid) {
  const ref = doc(db, "usuarios", uid, "notifs", "summary");
  await setDoc(
    ref,
    {
      chatUnread: increment(1),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}



export default function ChatDetail() {
  const { chatId } = useParams(); // userId
  const navigate = useNavigate();

  const [chatInfo, setChatInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newText, setNewText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  // Cargar info del chat (nombre, email, etc.)
  useEffect(() => {
    const loadChat = async () => {
      try {
        const ref = doc(db, "chats", chatId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setChatInfo({ id: snap.id, ...snap.data() });
          // Marcar como leído para admin
          await updateDoc(ref, { unreadForAdmin: false });
        } else {
          setChatInfo(null);
        }
      } catch (e) {
        console.error("Error cargando chat:", e);
      }
    };
    loadChat();
  }, [chatId]);

  // Escuchar mensajes en tiempo real
  useEffect(() => {
    const msgsRef = collection(db, "chats", chatId, "messages");
    const q = query(msgsRef, orderBy("createdAt", "asc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMessages(list);
        setLoading(false);
        // scroll al final
        setTimeout(() => {
          bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      },
      (err) => {
        console.error("Error leyendo mensajes:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [chatId]);

  const handleSend = async (e) => {
    e?.preventDefault();
    const text = newText.trim();
    if (!text) return;

    try {
      setSending(true);
      const currentUser = auth.currentUser;
      const senderId = currentUser?.uid || "admin";

      const msgsRef = collection(db, "chats", chatId, "messages");
      await addDoc(msgsRef, {
        senderId,
        senderType: "admin",
        text,
        createdAt: serverTimestamp(),
      });

    // Actualizar metadata del chat
    const chatRef = doc(db, "chats", chatId);
    await updateDoc(chatRef, {
      lastMessage: text,
      lastMessageAt: serverTimestamp(),
      lastSenderType: "admin",
      unreadForUser: true,
      unreadForAdmin: false,
    }); 

   

    // ✅ Notificación al usuario SOLO si el último mensaje NO fue mío
    const uid = chatId;

     notifyChat(uid);  //Para marcar notificacion pendiente

    const ultimoFueMio = chatInfo?.lastSenderType === "admin";

    if (uid && !ultimoFueMio) {
      try {
        await enviarNotificacion({
          titulo: "Support",
          descripcion: text,
          envioMultiple: false,
          usuarioId: uid,
        });
      } catch (e) {
        console.warn("No se pudo enviar notificación:", e?.message || e);
      }
    }


      setNewText("");
    } catch (e) {
      console.error("Error al enviar mensaje:", e);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    if (ts.toDate) {
      const d = ts.toDate();
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return "";
  };

  if (loading && !chatInfo) {
    return <p>Cargando chat...</p>;
  }

  if (!chatInfo) {
    return (
      <div>
        <button onClick={() => navigate(-1)} style={{ marginBottom: "1rem" }}>
          ← Volver
        </button>
        <p>No se encontró este chat.</p>
      </div>
    );
  }

  const irAUserDetail = () => {
    const uid = chatInfo?.userId || chatInfo?.uid || ""; // fallback por si lo nombraste distinto
    if (uid) {
      navigate(`/users/${uid}`);
    } else {
      alert("Este chat no tiene userId guardado.");
    }
  };

  const irCrearCancion = () => {
    const uid = chatInfo?.userId || chatInfo?.uid || ""; // fallback por si lo nombraste distinto
    if (uid) {
      navigate(`/crear-desde-usuario/${uid}`);
    } else {
      alert("Este chat no tiene userId guardado.");
    }
  };


  const headerName =
    chatInfo.userName || chatInfo.userEmail || `Usuario ${chatId}`;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 80px)",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "0.75rem 1rem",
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
        }}
      >
        <button onClick={() => navigate(-1)}>←</button>
                <div
          onClick={irAUserDetail}
          title="Ver usuario"
          style={{
            cursor: chatId ? "pointer" : "default",
            padding: "6px 8px",
            borderRadius: 10,
          }}
        >
          <div style={{ fontWeight: 600, textDecoration: chatId ? "underline" : "none" }}>
            {headerName}
          </div>
          <div style={{ fontSize: "0.85rem", color: "#6b7280" }}>
            {chatInfo.userEmail || ""}
          </div>
        </div>

        <button
          className="btn"
          type="button"
          onClick={irCrearCancion}
        >
          Crear nueva (desde usuario)
        </button>


      </div>

      {/* Mensajes */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "1rem",
          background: "#F3F4F6",
        }}
      >
        {messages.length === 0 && (
          <p style={{ color: "#6b7280" }}>No hay mensajes aún.</p>
        )}

        {messages.map((m) => {
          const isAdmin = m.senderType === "admin";
          return (
            <div
              key={m.id}
              style={{
                display: "flex",
                justifyContent: isAdmin ? "flex-end" : "flex-start",
                marginBottom: "0.5rem",
              }}
            >
              <div
                style={{
                  maxWidth: "70%",
                  padding: "0.5rem 0.75rem",
                  borderRadius: "0.75rem",
                  background: isAdmin ? "#111827" : "white",
                  color: isAdmin ? "white" : "#111827",
                  fontSize: "0.9rem",
                }}
              >
                <div>{m.text}</div>
                <div
                  style={{
                    fontSize: "0.7rem",
                    marginTop: 2,
                    textAlign: "right",
                    opacity: 0.8,
                  }}
                >
                  {formatTime(m.createdAt)}
                </div>
              </div>
            </div>
          );
        })}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form
        onSubmit={handleSend}
        style={{
          padding: "0.75rem",
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          gap: "0.5rem",
          background: "white",
        }}
      >
        <input
          type="text"
          placeholder="Escribe un mensaje..."
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          style={{
            flex: 1,
            padding: "0.5rem 0.75rem",
            borderRadius: "999px",
            border: "1px solid #d1d5db",
          }}
        />
        <button
          type="submit"
          disabled={sending || !newText.trim()}
          style={{
            padding: "0.5rem 1rem",
            borderRadius: "999px",
            border: "none",
            background: "#111827",
            color: "white",
            cursor: "pointer",
          }}
        >
          Enviar
        </button>
      </form>
    </div>
  );
}
